// Estado e chamadas dos botoes de bot (Brainnect). Sem JSX.
// API: /brainnect/ui/* autenticada com o token de SESSAO do Stoat (header x-session-token).
import { createSignal } from "solid-js";
import { createStore } from "solid-js/store";

const BASE = `${location.origin}/brainnect`;

export interface UiButton {
  text: string;
  cb?: boolean;
  url?: string;
  web_app_url?: string;
  game?: boolean;
}
export interface MsgMarkup {
  bot_id: string;
  rows: UiButton[][];
}
export interface ReplyKb {
  bot_id: string;
  kind: "reply" | "force_reply";
  rows?: { text: string }[][];
  resize?: boolean;
  one_time?: boolean;
  persistent?: boolean;
  placeholder?: string;
}
export type BotDialog = { kind: "alert"; text: string } | { kind: "link"; url: string };

/** Token de sessao do usuario (varias formas, conforme a versao do stoat.js). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sessionToken(client: any, state: any): string | undefined {
  const h = client?.authenticationHeader;
  if (Array.isArray(h) && typeof h[1] === "string" && h[0] === "x-session-token") return h[1];
  if (typeof client?.session?.token === "string") return client.session.token;
  const s = state?.auth?.getSession?.();
  return typeof s?.token === "string" ? s.token : undefined;
}

// ---------- teclados inline por mensagem ----------
const [markups, setMarkups] = createStore<Record<string, MsgMarkup | false>>({});
/** undefined = ainda nao buscou, false = buscou e nao tem botoes */
export const markupOf = (messageId: string) => markups[messageId];

const queue = new Map<string, { ids: Set<string>; token: string }>();
let timer: ReturnType<typeof setTimeout> | undefined;

export function requestMarkup(channel: string, id: string, token: string, force = false) {
  if (!token || (!force && markups[id] !== undefined)) return;
  let q = queue.get(channel);
  if (!q) { q = { ids: new Set(), token }; queue.set(channel, q); }
  q.ids.add(id);
  if (!timer) timer = setTimeout(flush, 100);
}

async function flush() {
  timer = undefined;
  const items = [...queue.entries()];
  queue.clear();
  for (const [channel, q] of items) {
    const ids = [...q.ids];
    for (let i = 0; i < ids.length; i += 50) {
      const chunk = ids.slice(i, i + 50);
      try {
        const r = await fetch(`${BASE}/ui/chat?channel=${channel}&messages=${chunk.join(",")}`, { headers: { "x-session-token": q.token } });
        if (!r.ok) continue; // tenta de novo na proxima montagem
        const j = await r.json();
        const found = j?.result?.messages ?? {};
        for (const id of chunk) setMarkups(id, found[id] ?? false);
      } catch { /* sem rede: ignora */ }
    }
  }
}

// ---------- cliques ----------
const [busy, setBusy] = createStore<Record<string, boolean>>({});
export const isBusy = (m: string, r: number, c: number) => !!busy[`${m}:${r}:${c}`];

export async function pressButton(token: string | undefined, channel: string, message: string, row: number, col: number) {
  const key = `${message}:${row}:${col}`;
  if (!token || busy[key]) return;
  setBusy(key, true);
  try {
    const r = await fetch(`${BASE}/ui/callback`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-session-token": token },
      body: JSON.stringify({ channel, message, row, col }),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j?.ok) {
      showToast(r.status === 429 ? "Muitos cliques. Aguarde um instante." : r.status === 503 ? "O bot está offline." : "Não foi possível enviar o clique.");
      return;
    }
    const a = j.result ?? {};
    if (a.answered && a.text) { if (a.show_alert) setDialog({ kind: "alert", text: String(a.text) }); else showToast(String(a.text)); }
    if (a.answered && a.url) openExternal(String(a.url));
  } catch {
    showToast("Sem conexão com o Brainnect.");
  } finally {
    setBusy(key, false);
  }
}

// ---------- toast e dialogos ----------
const [toast, setToast] = createSignal<string | null>(null);
const [dialog, setDialog] = createSignal<BotDialog | null>(null);
export { toast, dialog };
let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function showToast(text: string) {
  setToast(text);
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => setToast(null), 3000);
}
export const closeDialog = () => setDialog(null);
/** Links externos sempre pedem confirmacao. */
export const openExternal = (url: string) => setDialog({ kind: "link", url });
export function confirmLink(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
  setDialog(null);
}

// ---------- teclado de resposta (por canal) ----------
const [kbs, setKbs] = createStore<Record<string, { kb: ReplyKb | null; sig: string; open: boolean }>>({});
export const kbOf = (channel: string) => kbs[channel];
export const hasReplyKeyboard = (channel: string) => kbs[channel]?.kb?.kind === "reply" && !!kbs[channel]?.kb?.rows?.length;
export const toggleReplyKeyboard = (channel: string) => { if (kbs[channel]) setKbs(channel, "open", (o) => !o); };
export const closeReplyKeyboard = (channel: string) => { if (kbs[channel]) setKbs(channel, "open", false); };

export async function loadKeyboard(channel: string, token: string) {
  try {
    const r = await fetch(`${BASE}/ui/chat?channel=${channel}&messages=`, { headers: { "x-session-token": token } });
    if (!r.ok) return;
    const body = (await r.json())?.result;
    setMenus(channel, body?.menu ?? null);
    const kb: ReplyKb | null = body?.reply_keyboard ?? null;
    const sig = JSON.stringify(kb);
    if (kbs[channel]?.sig === sig) return;
    setKbs(channel, { kb, sig, open: true });
  } catch { /* ignora */ }
}

// ---------- mini app ----------
export interface MiniAppSession {
  url: string;
  initData: string;
  botName: string;
  botId: string;
  gameToken?: string;
}
const [miniApp, setMiniApp] = createSignal<MiniAppSession | null>(null);
export { miniApp };
export const closeMiniApp = () => setMiniApp(null);

export async function launchWebApp(token: string | undefined, channel: string, message: string, row: number, col: number) {
  const key = `${message}:${row}:${col}`;
  if (!token || busy[key]) return;
  setBusy(key, true);
  try {
    const r = await fetch(`${BASE}/ui/webapp`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-session-token": token },
      body: JSON.stringify({ channel, message, row, col }),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j?.ok) {
      showToast(r.status === 429 ? "Muitos cliques. Aguarde um instante." : r.status === 503 ? "O bot está offline." : "Não foi possível abrir o aplicativo.");
      return;
    }
    const res = j.result ?? {};
    let origin = "";
    try { origin = new URL(res.url).origin; } catch { /* ignora */ }
    // so https e nunca a origem do proprio cliente (a pagina teria acesso a sessao)
    if (!origin.startsWith("https://") || origin === location.origin) {
      showToast("Este endereço não pode ser aberto.");
      return;
    }
    setMiniApp({ url: String(res.url), initData: String(res.init_data), botName: String(res.bot?.username ?? ""), botId: String(res.bot?.id ?? ""), gameToken: res.game?.token ? String(res.game.token) : undefined });
  } catch {
    showToast("Sem conexão com o Brainnect.");
  } finally {
    setBusy(key, false);
  }
}

// ---------- botao de menu do bot (abre o Web App cadastrado no BotFather) ----------
export interface MenuButton {
  bot_id: string;
  text: string;
}
const [menus, setMenus] = createStore<Record<string, MenuButton | null>>({});
export const menuOf = (channel: string) => menus[channel] ?? null;

export async function launchMenuApp(token: string | undefined, channel: string) {
  if (!token) return;
  try {
    const r = await fetch(`${BASE}/ui/webapp`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-session-token": token },
      body: JSON.stringify({ channel, menu: true }),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j?.ok) { showToast("Não foi possível abrir o aplicativo."); return; }
    const res = j.result ?? {};
    let origin = "";
    try { origin = new URL(res.url).origin; } catch { /* ignora */ }
    if (!origin.startsWith("https://") || origin === location.origin) { showToast("Este endereço não pode ser aberto."); return; }
    setMiniApp({ url: String(res.url), initData: String(res.init_data), botName: String(res.bot?.username ?? ""), botId: String(res.bot?.id ?? "") });
  } catch {
    showToast("Sem conexão com o Brainnect.");
  }
}

// ---------- jogos: pontuacao enviada pelo jogo (Callnnect.Game.postScore) ----------
export async function postGameScore(token: string | undefined, gameToken: string | undefined, score: number) {
  if (!token || !gameToken) return;
  try {
    const r = await fetch(`${BASE}/ui/score`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-session-token": token },
      body: JSON.stringify({ token: gameToken, score }),
    });
    const j = await r.json().catch(() => null);
    if (r.ok && j?.result?.new_best) showToast(`Nova melhor pontuação: ${j.result.best}`);
  } catch { /* sem rede: ignora */ }
}
